import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import useSEO from '@/hooks/useSEO';
import ConsentCheckboxes from '@/components/ConsentCheckboxes';

const ContactPage = () => {
  useSEO('contact', 'Contact Us | We Have Your Car');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [nonMarketingConsent, setNonMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({
    map_lat: 30.166498,
    map_lng: -97.769819,
    map_zoom: 15
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        marketing_consent: marketingConsent,
        non_marketing_consent: nonMarketingConsent
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setMarketingConsent(false);
      setNonMarketingConsent(false);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] py-8" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 font-['Exo_2']">
            Contact <span className="gradient-text">Us</span>
          </h1>
          <p className="text-[#a8b8d0]">
            Have questions? We're here to help!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="bg-[#132238] rounded-xl p-8 border border-[#1a2d47] mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <a 
                  href="tel:5126660016" 
                  className="flex items-start gap-4 p-4 bg-[#1a2d47]/50 rounded-lg hover:bg-[#1a4d8c]/30 transition-colors"
                  data-testid="contact-phone"
                >
                  <div className="bg-[#ff6b35]/20 p-3 rounded-full">
                    <Phone className="text-[#ff6b35]" size={24} />
                  </div>
                  <div>
                    <p className="text-[#a8b8d0] text-sm">Phone</p>
                    <p className="text-white font-semibold text-lg">(512) 666-0016</p>
                  </div>
                </a>

                <a 
                  href="mailto:info@wehaveyourcar.com"
                  className="flex items-start gap-4 p-4 bg-[#1a2d47]/50 rounded-lg hover:bg-[#1a4d8c]/30 transition-colors"
                  data-testid="contact-email"
                >
                  <div className="bg-[#ff6b35]/20 p-3 rounded-full">
                    <Mail className="text-[#ff6b35]" size={24} />
                  </div>
                  <div>
                    <p className="text-[#a8b8d0] text-sm">Email</p>
                    <p className="text-white font-semibold text-lg">info@wehaveyourcar.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-[#1a2d47]/50 rounded-lg">
                  <div className="bg-[#ff6b35]/20 p-3 rounded-full">
                    <MapPin className="text-[#ff6b35]" size={24} />
                  </div>
                  <div>
                    <p className="text-[#a8b8d0] text-sm">Address</p>
                    <p className="text-white font-semibold">8203 Bluff Springs Rd, Unit 4</p>
                    <p className="text-white">Austin, TX 78744</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#1a2d47]/50 rounded-lg">
                  <div className="bg-[#ff6b35]/20 p-3 rounded-full">
                    <Clock className="text-[#ff6b35]" size={24} />
                  </div>
                  <div>
                    <p className="text-[#a8b8d0] text-sm">Business Hours</p>
                    <p className="text-white font-semibold">Monday - Saturday: 10AM - 6PM</p>
                    <p className="text-white">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-[#132238] rounded-xl overflow-hidden border border-[#1a2d47] h-[300px]">
              <iframe
                src={`https://maps.google.com/maps?q=${settings.map_lat},${settings.map_lng}&z=${settings.map_zoom || 15}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="We Have Your Car - Location Map"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#132238] rounded-xl p-8 border border-[#1a2d47]">
            <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[#a8b8d0] text-sm mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-3 text-white placeholder-[#a8b8d0]"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-[#a8b8d0] text-sm mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-3 text-white placeholder-[#a8b8d0]"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-[#a8b8d0] text-sm mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(512) 555-0123"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-3 text-white placeholder-[#a8b8d0]"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label className="block text-[#a8b8d0] text-sm mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="How can we help you?"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-3 text-white placeholder-[#a8b8d0] resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              {/* Consent Checkboxes */}
              <ConsentCheckboxes
                marketingConsent={marketingConsent}
                setMarketingConsent={setMarketingConsent}
                nonMarketingConsent={nonMarketingConsent}
                setNonMarketingConsent={setNonMarketingConsent}
              />

              <button
                type="submit"
                disabled={submitting}
                className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {submitting ? 'Sending...' : 'Submit'}
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
